import {
    destroyChatbot,
    initChatbot,
    loadChatbotBootstrap,
    resetChatbot,
} from '../ai/chatbot';

declare const howspace: any;

/** The latest view initialisation of each widget. One that was destroyed while loading must not render. */
const viewLoads = new WeakMap<HTMLElement, symbol>();
/**
 * The initialisation each widget is running. A step left and shown again quickly starts a new one before the old
 * one is done, so they run one after another and the old one is torn down before the new one starts.
 */
const viewInits = new WeakMap<HTMLElement, Promise<void>>();

const getPageSelect = (widget: any) => widget
    .find('.searchable-select-values[data-name="source_page"]')
    .closest('.howspace-searchable-select');

const getPageSelectToggle = (widget: any) => getPageSelect(widget).find('button[data-toggle="dropdown"]');

const initViewAfter = async (
    previousInit: Promise<void> | undefined,
    _widget: any,
    widgetElement: HTMLElement,
    container: HTMLElement,
    load: symbol,
): Promise<void> => {
    await previousInit?.catch(() => undefined);
    if (viewLoads.get(widgetElement) !== load) {
        return;
    }

    // The bootstrap rendered with the page is used once. A path step shown again fetches the thread anew.
    const bootstrap = await loadChatbotBootstrap(container).catch(error => {
        console.error('Coach widget: could not load the chat', error);
        return null;
    });
    if (viewLoads.get(widgetElement) !== load) {
        container.querySelector('.howspace-ai-chat-loading')?.remove();
        return;
    }
    if (!bootstrap) {
        return;
    }

    await initChatbot(widgetElement, container, bootstrap);
    if (viewLoads.get(widgetElement) !== load) {
        destroyChatbot(widgetElement);
        return;
    }

    _widget.find('.reset-thread').off('click.coachAi2').on('click.coachAi2', () => {
        resetChatbot(widgetElement, container).catch(error => {
            console.error('Coach widget: could not reset thread', error);
        });
    });
};

howspace.widgets.coach = {
    initView: async (_widget: any) => {
        const widgetElement = _widget.get(0) as HTMLElement;
        const container = widgetElement.querySelector<HTMLElement>('.howspace-ai-chatbot');
        if (!container) {
            return;
        }

        const load = Symbol('coach view');
        viewLoads.set(widgetElement, load);
        const previousInit = viewInits.get(widgetElement);
        const init = initViewAfter(previousInit, _widget, widgetElement, container, load);
        viewInits.set(widgetElement, init);
        await init;
    },

    destroyView: (_widget: any) => {
        viewLoads.delete(_widget.get(0) as HTMLElement);
        _widget.find('.reset-thread').off('click.coachAi2');
        destroyChatbot(_widget.get(0) as HTMLElement);
    },

    initEdit: (_widget: any) => {
        const pageSelect = getPageSelect(_widget);
        if (!pageSelect.length) {
            return;
        }

        // Clear the validation state as soon as the admin picks a page or switches the source.
        const clear = () => howspace.form.clearInvalidField(getPageSelectToggle(_widget));
        pageSelect.get(0).addEventListener(howspace.components.searchableSelect.CHANGE, clear);
        _widget.find('input[data-name="source_data_scope"]').on('change', clear);
    },
    destroyEdit: () => {},

    save: (widget: any, dataPayload: any) => {
        const pageSelectToggle = getPageSelectToggle(widget);
        if (pageSelectToggle.length) {
            howspace.form.clearInvalidField(pageSelectToggle);

            // The "Selected page" source is meaningless without a page and the API rejects it with a
            // developer-facing message. Keep the admin in the editor and point at the field instead.
            if (dataPayload.source_data_scope === 'page' && !dataPayload.source_page) {
                howspace.form.markInvalidField(pageSelectToggle);
                // The save button's loading state disables every control in the widget, so focus only
                // lands once the generic save handler has reset it.
                requestAnimationFrame(() => pageSelectToggle.get(0).focus());
                return false;
            }
        }

        const sourceWidgets: Array<{type: string; id: string}> = [];
        widget.find('.howspace-item-picker .selected-items .item').each((index: number, element: HTMLElement) => {
            const widgetType = element.getAttribute('data-type') || '';
            const rawWidgetId = element.getAttribute('data-id') || '';
            sourceWidgets.push({type: widgetType, id: rawWidgetId.replace(`${widgetType}-`, '')});
        });
        dataPayload.sources = sourceWidgets;
        delete dataPayload.items;
        return dataPayload;
    },
};
